import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Machine } from '@shared/entities/machine';
import { MachineSFContactIdService } from '@shared/services/api/machine-sfcontact-id.service';
import { MachinesAccountIdsService } from '@shared/services/api/machines-account-ids.service';
import { first, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-machines-selection',
  templateUrl: './machines-selection.component.html',
  styleUrl: './machines-selection.component.scss'
})
export class MachinesSelectionComponent implements OnChanges, OnDestroy {
  @Input() form!: FormGroup;
  @Input() isCurrentUserAdmin = false;
  @Input() isLight = true;
  @Input() sfContactId = '';
  @Input() accountIds: string[] = [];
  @Input() isPartner = false;
  @Input() isEdit = false;
  @Input() existingMachines: Machine[] = [];

  machinesByAccount: { accountId: string; accountName: string; machines: Machine[] }[] = [];
  selectedMachines: Machine[] = [];
  isMachinesLoaded = false;

  @Output() machineSelectionChange = new EventEmitter<{ machine: Machine; checked: boolean }>();
  @Output() machinesLoaded = new EventEmitter<Machine[]>();

  private destroy$ = new Subject<void>();

  constructor(
    private _machineSFContactIdService: MachineSFContactIdService,
    private _machinesAccountIdsService: MachinesAccountIdsService,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isPartner) {
      if (changes['accountIds']) {
        const current = JSON.stringify(this.accountIds || []);
        const previous = JSON.stringify(changes['accountIds'].previousValue || []);

        if (current !== previous) {
          if (this.accountIds && this.accountIds.length > 0) {
            this.loadMachinesByAccountIds(this.accountIds);
          } else {
            this.clearMachines();
          }
        }
      }
      return;
    }

    if (changes['sfContactId']) {
      const currentValue = changes['sfContactId'].currentValue;
      const previousValue = changes['sfContactId'].previousValue;

      if (currentValue && currentValue !== previousValue) {
        this.loadMachinesForContact(currentValue);
      } else if (!currentValue && previousValue) {
        this.clearMachines();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isMachineSelected(machineId: string): boolean {
    return this.selectedMachines.some((machine) => machine.id === machineId);
  }

  onMachineSelection(machine: Machine, checked: boolean): void {
    this.machineSelectionChange.emit({ machine, checked });
  }

  private loadMachinesForContact(sfContactId: string): void {
    this.machinesByAccount = [];
    this.selectedMachines = [];

    this._machineSFContactIdService
      .getMachinesBySFContactId(sfContactId)
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe((machines: Machine[]) => {
        this.processMachines(machines);
        this.isMachinesLoaded = true;
        this._cdr.detectChanges();
      });
  }

  private loadMachinesByAccountIds(accountIds: string[]): void {
    if (!accountIds || accountIds.length === 0) {
      this.clearMachines();
      return;
    }

    this.isMachinesLoaded = false;

    this._machinesAccountIdsService
      .getMachinesByAccountIds(accountIds)
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe((machines: Machine[]) => {
        this.processMachines(machines);
        this.isMachinesLoaded = true;
      });
  }

  private processMachines(machines: Machine[]): void {
    const machinesMap = new Map<string, { accountId: string; accountName: string; machines: Machine[] }>();

    machines.forEach((machine: Machine) => {
      // If machine has no accountId, use a default group
      const accountId = machine.accountId || 'no-account';
      const accountName = machine.accountName || 'Unassigned';

      if (!machinesMap.has(accountId)) {
        machinesMap.set(accountId, {
          accountId: accountId,
          accountName: accountName,
          machines: []
        });
      }

      machinesMap.get(accountId)?.machines.push(machine);
    });

    this.machinesByAccount = Array.from(machinesMap.values());

    const allMachines = this.machinesByAccount.flatMap((group) => group.machines);

    // If editing and we have existing machines, use those, otherwise select all
    if (this.isEdit) {
      // In modifica: seleziona solo le macchine già associate all'utente.
      // Se existingMachines è vuoto, nessuna macchina viene pre-selezionata.
      if (this.existingMachines && this.existingMachines.length > 0) {
        const validMachineIds = allMachines.map((m) => m.id);
        this.selectedMachines = this.existingMachines.filter((m) => validMachineIds.includes(m.id));
      } else {
        this.selectedMachines = [];
      }
    } else {
      // In creazione: pre-seleziona tutte le macchine disponibili
      this.selectedMachines = allMachines;
    }

    this.form.patchValue({ selectedMachines: this.selectedMachines });
    this.machinesLoaded.emit(this.selectedMachines);
  }

  private clearMachines(): void {
    this.machinesByAccount = [];
    this.selectedMachines = [];
    this.form.patchValue({ selectedMachines: [] });
    this.isMachinesLoaded = true;
    this.machinesLoaded.emit([]);
  }
}
